import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Loader from "../../components/loader";
import MDEditor from "@uiw/react-md-editor";
import api from "../../services/api";

export default function TemplateView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contractTemplate, setContractTemplate] = useState();
  const [values, setValues] = useState({});
  const [content, setContent] = useState("");
  const [variables, setVariables] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const saveTimeout = useRef();
  const contentRef = useRef("");
  const variablesRef = useRef([]);

  const fetchContractTemplate = async () => {
    const { data, ok } = await api.get(`/legal_document_template/${id}`);
    if (!ok) return toast.error("Template not found");
    setContractTemplate(data);
    setValues({
      name: data.name,
      instruction: data.instruction,
      language: data.language,
      category: data.category,
      business_unit_id: data.business_unit_id || "",
      business_unit_name: data.business_unit_name || "",
    });
    setContent(data.content || "");
    contentRef.current = data.content || "";
    setVariables(data.variables || []);
    variablesRef.current = data.variables || [];
  };

  const fetchBusinessUnits = async () => {
    const { data, ok } = await api.get("/business_unit");
    if (ok && data) setBusinessUnits(data);
  };

  useEffect(() => {
    fetchContractTemplate();
    fetchBusinessUnits();
  }, [id]);

  const save = async (fields) => {
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      const { data, ok } = await api.put(`/legal_document_template/${id}`, fields);
      if (!ok) return toast.error("Failed to save");
      setContractTemplate(data);
    }, 800);
  };

  const handleChange = (field, value) => {
    const updated = { ...values, [field]: value };
    setValues(updated);
    save({ ...updated, content: contentRef.current, variables: variablesRef.current });
  };

  const handleBUChange = (buId) => {
    const bu = businessUnits.find((b) => b._id === buId);
    const updated = { ...values, business_unit_id: buId, business_unit_name: bu?.name || "" };
    setValues(updated);
    save({ ...updated, content: contentRef.current, variables: variablesRef.current });
  };

  const handleContentChange = (value) => {
    contentRef.current = value;
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      const { data, ok } = await api.put(`/legal_document_template/${id}`, { ...values, content: value, variables: variablesRef.current });
      if (!ok) return toast.error("Failed to save");
      setContractTemplate(data);
    }, 800);
  };

  const handleVariablesChange = (newVars) => {
    setVariables(newVars);
    variablesRef.current = newVars;
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      const { data, ok } = await api.put(`/legal_document_template/${id}`, { ...values, content: contentRef.current, variables: newVars });
      if (!ok) return toast.error("Failed to save");
      setContractTemplate(data);
    }, 800);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this template?")) return;
    const { ok } = await api.remove(`/legal_document_template/${id}`);
    if (!ok) return toast.error("Failed to delete");
    toast.success("Template deleted");
    navigate("/legal_document/templates");
  };

  if (!contractTemplate) return <Loader size="small" />;

  return (
    <div className="h-full">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigate("/legal_document/templates")} className="text-sm text-blue-600 hover:underline">
          &larr; Back to templates
        </button>
        <button onClick={handleDelete} className="px-3 py-1 rounded-md text-white bg-red-500 text-sm">
          Delete
        </button>
      </div>

      <div className="flex gap-4 h-[calc(100vh-160px)]">
        <div className="w-4/5 bg-white rounded-lg border border-gray-200 flex flex-col overflow-hidden" data-color-mode="light">
          <MDEditor
            value={content}
            onChange={(val) => handleContentChange(val || "")}
            height="100%"
            preview="live"
            visibleDragbar={false}
          />
        </div>

        <div className="w-1/5 flex flex-col gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Name</label>
            <input
              type="text"
              value={values.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Category</label>
            <input
              type="text"
              value={values.category || ""}
              onChange={(e) => handleChange("category", e.target.value)}
              placeholder="e.g. NDA, Employment, Freelance..."
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Business Unit</label>
            <select
              value={values.business_unit_id || ""}
              onChange={(e) => handleBUChange(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
              <option value="">-</option>
              {businessUnits.map((bu) => (
                <option key={bu._id} value={bu._id}>{bu.emoji ? `${bu.emoji} ` : ""}{bu.name}</option>
              ))}
            </select>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Language</label>
            <select
              value={values.language || "fr"}
              onChange={(e) => handleChange("language", e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
              <option value="fr">Fran&#231;ais</option>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
              <option value="es">Espa&#241;ol</option>
            </select>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Instruction</label>
            <textarea
              value={values.instruction || ""}
              onChange={(e) => handleChange("instruction", e.target.value)}
              placeholder="Instructions for this contract..."
              rows={4}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm resize-none"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 flex-1 overflow-auto">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-gray-500 uppercase">Variables</label>
              <button
                onClick={() => handleVariablesChange([...variables, { name: "", description: "" }])}
                className="text-xs text-blue-600 hover:underline">
                + Add
              </button>
            </div>
            <div className="space-y-2">
              {variables.map((v, i) => (
                <div key={i} className="flex gap-1 items-start">
                  <div className="flex-1 space-y-1">
                    <input
                      type="text"
                      value={v.name}
                      onChange={(e) => {
                        const updated = [...variables];
                        updated[i] = { ...updated[i], name: e.target.value };
                        handleVariablesChange(updated);
                      }}
                      placeholder="name"
                      className="w-full border border-gray-300 rounded px-2 py-0.5 text-xs"
                    />
                    <input
                      type="text"
                      value={v.description}
                      onChange={(e) => {
                        const updated = [...variables];
                        updated[i] = { ...updated[i], description: e.target.value };
                        handleVariablesChange(updated);
                      }}
                      placeholder="description"
                      className="w-full border border-gray-300 rounded px-2 py-0.5 text-xs text-gray-500"
                    />
                  </div>
                  <button
                    onClick={() => handleVariablesChange(variables.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-600 text-xs mt-0.5">
                    &times;
                  </button>
                </div>
              ))}
              {variables.length === 0 && <p className="text-xs text-gray-400 italic">No variables</p>}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 text-xs text-gray-500 space-y-1">
            <div>Updated: {new Date(contractTemplate.updated_at).toLocaleString()}</div>
            <div>Created: {new Date(contractTemplate.created_at).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
